import React from 'react'
import {Formik, Form, Field, ErrorMessage} from 'formik'
import * as Yup from 'yup'
import {CreateDeviceFormValuesModel} from '../../../../entities/component.entity'
import {DeviceTypeModel} from '../../../../entities/device.entity'
import Astericsign from '../forms/Astericsign'
import {
  imeiNumberValidation,
  modelNumberValidation,
} from '../forms/helper/ValidationHelperComponent'
import PrimaryButton from '../common/buttons/PrimaryButton'

interface EditDeviceModalProps {
  isOpen: boolean
  onClose: () => void
  handleSubmit: (values: CreateDeviceFormValuesModel, resetForm: () => void) => void
  deviceTypes: DeviceTypeModel[]
  initialValues: CreateDeviceFormValuesModel
  isLoading: any
}

const EditDeviceModal: React.FC<EditDeviceModalProps> = ({
  isOpen,
  onClose,
  handleSubmit,
  deviceTypes,
  initialValues,
  isLoading = false,
}) => {
  if (!isOpen) return null

  return (
    <div className='modal fade show d-block' tabIndex={-1}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Edit Device</h5>
            <button type='button' className='btn-close' onClick={onClose}></button>
          </div>
          <div className='modal-body' style={{maxHeight: '400px', overflowY: 'auto'}}>
            <Formik
              initialValues={initialValues}
              validationSchema={Yup.object({
                name: Yup.string().trim(),
                deviceType: Yup.string().required('Device Type is required'),
                imeiNumber: imeiNumberValidation,
                modelNumber: modelNumberValidation,
                ownershipType: Yup.string().oneOf(['loaned', 'rental', 'purchased']).required('Ownership is required'),
              })}
              onSubmit={(values, {resetForm}) => handleSubmit(values, resetForm)}
            >
              {({values, setFieldValue}) => (
                <Form>
                  <div className='mb-3'>
                    <label className='form-label'>
                      Device Type <Astericsign />
                    </label>
                    <Field as='select' name='deviceType' className='form-control'>
                      <option value=''>Select device</option>
                      {deviceTypes.map((device) => (
                        <option key={device.id} value={device.id}>
                          {device.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name='deviceType' component='div' className='text-danger' />
                  </div>

                  {[
                    {name: 'imeiNumber', label: 'IMEI Number'},
                    {name: 'name', label: 'Device Name', type: 'text'},
                    {name: 'modelNumber', label: 'Model Number'},
                    // {name: 'description', label: 'Description'},
                    // {name: 'price', label: 'Price', type: 'number'},
                  ].map(({name, label, type = 'text'}) => (
                    <div className='mb-3' key={name}>
                      <label className='form-label'>
                        {label} {label !== 'Device Name' && <Astericsign />}
                      </label>
                      <Field
                        type={type}
                        name={name}
                        className='form-control'
                        {...(name === 'imeiNumber' && {
                          maxLength: 15,
                          pattern: '\\d{15}',
                          inputMode: 'numeric',
                          title: 'IMEI must be exactly 15 digits',
                          onInput: (e: React.FormEvent<HTMLInputElement>) => {
                            e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '')
                          },
                          // On blur: if 14 digits and starts non-zero, prepend "0" to make 15.
                          // (Recovers leading zeros stripped by Excel/CSV imports.)
                          // Note: edit form does not auto-fill device name to avoid clobbering it.
                          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                            let val = (e.currentTarget.value || '').replace(/\D/g, '')
                            if (val.length === 14 && val[0] !== '0') {
                              val = '0' + val
                              setFieldValue('imeiNumber', val)
                            }
                          },
                        })}
                      />
                      <ErrorMessage name={name} component='div' className='text-danger' />
                    </div>
                  ))}

                  <div className='mb-3'>
                    <label className='form-label'>Serial Number</label>
                    <Field
                      type='text'
                      name='serialNumber'
                      className='form-control'
                      placeholder='Optional'
                    />
                    <ErrorMessage name='serialNumber' component='div' className='text-danger' />
                  </div>

                  <div className='mb-3'>
                    <label className='form-label'>
                      Ownership <Astericsign />
                    </label>
                    <Field as='select' name='ownershipType' className='form-control'>
                      <option value='loaned'>Loaned</option>
                      <option value='rental'>Rented</option>
                      <option value='purchased'>Owned</option>
                    </Field>
                    <ErrorMessage name='ownershipType' component='div' className='text-danger' />
                  </div>

                  <div className='modal-footer'>
                    <PrimaryButton type='submit' disabled={isLoading}>
                      {isLoading ? 'Saving...' : 'Save Changes'}
                    </PrimaryButton>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditDeviceModal
